import { useState } from 'react'
import Input from '@/components/Input'
import Label from '@/components/Label'
import { CarModelSelector } from '@/components/driver/CarModelSelector'
import PlacesAutocomplete from '@/components/PlacesAutocomplete'
import { useLoadScript } from "@react-google-maps/api"
import { GOOGLE_MAPS_LIBRARIES } from '@/lib/googleMapsLibraries'

const DriverRegistrationForm = ({ 
    formData, 
    setFormData, 
    errors = [], 
    currentStep = 1,
    isLoaded = true,
    onPlaceSelected = null,
    agreedToTerms = false,
    onTermsChange = null,
    onShowTerms = null
}) => {
    const { isLoaded: mapsLoaded } = useLoadScript({
        googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_KEY,
        libraries: GOOGLE_MAPS_LIBRARIES,
    });

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    const handlePlaceSelected = (destination, latLng, zipCode) => {
        handleInputChange('address', destination)
        handleInputChange('postal_code', zipCode)
        if (onPlaceSelected) {
            onPlaceSelected(destination, latLng, zipCode)
        }
    }

    const getFieldError = (fieldName) => {
        return errors.find(error => error.toLowerCase().includes(fieldName.toLowerCase()))
    }

    const renderStep1 = () => (
        <div>
            {/* Name */}
            <div>
                <Label htmlFor="name">Prénom</Label>
                <Input
                    id="name"
                    type="text"
                    value={formData.name || ''}
                    className={`block mt-1 w-full ${getFieldError('nom') ? 'is-danger' : ''}`}
                    onChange={event => handleInputChange('name', event.target.value)}
                    required
                    autoFocus
                />
                {getFieldError('nom') && (
                    <p className="help is-danger">{getFieldError('nom')}</p>
                )}
            </div>
            
            <div className="mt-4">
                <Label htmlFor="lastname">Nom de famille</Label>
                <Input
                    id="lastname"
                    type="text"
                    value={formData.lastname || ''}
                    className={`block mt-1 w-full ${getFieldError('nom de famille') ? 'is-danger' : ''}`}
                    onChange={event => handleInputChange('lastname', event.target.value)}
                    required
                />
                {getFieldError('nom de famille') && (
                    <p className="help is-danger">{getFieldError('nom de famille')}</p>
                )}
            </div>

            {/* Email Address */}
            <div className="mt-4">
                <Label htmlFor="email">Courriel</Label>
                <Input
                    id="email"
                    type="email"
                    value={formData.email || ''}
                    className={`block mt-1 w-full ${getFieldError('courriel') ? 'is-danger' : ''}`}
                    onChange={event => handleInputChange('email', event.target.value)}
                    required
                />
                {getFieldError('courriel') && (
                    <p className="help is-danger">{getFieldError('courriel')}</p>
                )}
            </div>

            {/* Password */}
            <div className="mt-4">
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                    id="password"
                    type="password"
                    value={formData.password || ''}
                    className={`block mt-1 w-full ${getFieldError('mot de passe') ? 'is-danger' : ''}`}
                    onChange={event => handleInputChange('password', event.target.value)}
                    required
                    autoComplete="new-password"
                />
                {getFieldError('mot de passe') && (
                    <p className="help is-danger">{getFieldError('mot de passe')}</p>
                )}
            </div>

            {/* Confirm Password */}
            <div className="mt-4">
                <Label htmlFor="passwordConfirmation">Confirmer le mot de passe</Label>
                <Input
                    id="passwordConfirmation"
                    type="password"
                    value={formData.password_confirmation || ''}
                    className={`block mt-1 w-full ${getFieldError('mots de passe ne correspondent') ? 'is-danger' : ''}`}
                    onChange={event => handleInputChange('password_confirmation', event.target.value)}
                    required
                />
                {getFieldError('mots de passe ne correspondent') && (
                    <p className="help is-danger">{getFieldError('mots de passe ne correspondent')}</p>
                )}
            </div>
        </div>
    )

    const renderStep2 = () => (
        <div>
            {/* Address */}
            <div>
                <Label htmlFor="address">Adresse</Label>
                {(isLoaded && mapsLoaded) ? (
                    <PlacesAutocomplete 
                        setSelected={handlePlaceSelected} 
                        parentValue={formData.address || ''} 
                    />
                ) : null}
                {getFieldError('adresse') && (
                    <p className="help is-danger">{getFieldError('adresse')}</p>
                )}
            </div>

            {/* Phone */}
            <div className="mt-4">
                <Label htmlFor="phone">Numéro de téléphone</Label>
                <Input
                    id="phone"
                    type="tel"
                    value={formData.phone || ''}
                    className={`block mt-1 w-full ${getFieldError('téléphone') ? 'is-danger' : ''}`}
                    onChange={event => handleInputChange('phone', event.target.value)}
                    required
                />
                {getFieldError('téléphone') && (
                    <p className="help is-danger">{getFieldError('téléphone')}</p>
                )}
            </div>

            {/* Emergency Contact Name */}
            <div className="mt-4">
                <Label htmlFor="emergency-name">Contact d'urgence (nom)</Label>
                <Input
                    id="emergency-name"
                    type="text"
                    value={formData.emergency_name || ''}
                    className={`block mt-1 w-full ${getFieldError('contact d\'urgence') ? 'is-danger' : ''}`}
                    onChange={event => handleInputChange('emergency_name', event.target.value)}
                    required
                />
                {getFieldError('contact d\'urgence') && (
                    <p className="help is-danger">{getFieldError('contact d\'urgence')}</p>
                )}
            </div>

            {/* Emergency Contact Phone */}
            <div className="mt-4">
                <Label htmlFor="emergency-tel">Contact d'urgence (numéro de téléphone)</Label>
                <Input
                    id="emergency-tel"
                    type="tel"
                    value={formData.emergency_tel || ''}
                    className={`block mt-1 w-full ${getFieldError('contact d\'urgence') ? 'is-danger' : ''}`}
                    onChange={event => handleInputChange('emergency_tel', event.target.value)}
                    required
                />
                {getFieldError('contact d\'urgence') && (
                    <p className="help is-danger">{getFieldError('contact d\'urgence')}</p>
                )}
            </div>

            {/* Car Model Selector */}
            <div className="mt-4">
                <CarModelSelector 
                    value={formData.car_model || ''}
                    onChange={(value) => handleInputChange('car_model', value)}
                />
                {getFieldError('modèle de véhicule') && (
                    <p className="help is-danger">{getFieldError('modèle de véhicule')}</p>
                )}
            </div>

            {/* Terms and Conditions */}
            <div className="mt-4">
                <input
                    id="contract-checkbox"
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={onTermsChange}
                />
                <label htmlFor="contract-checkbox" className="checkbox ml-2">
                    J'accepte{' '}
                    <a
                        onClick={onShowTerms}
                        style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    >
                        les conditions générales du contrat
                    </a>
                </label>
            </div>
        </div>
    )

    const renderStep3 = () => (
        <h1 className="title is-4 has-text-success">
            Votre compte a bien été créé! Un membre de
            notre équipe vous contactera sous peu pour
            activer votre compte.
        </h1>
    )

    return (
        <div>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
        </div>
    )
}

export default DriverRegistrationForm
